import { useCallback, useEffect, useState } from 'react';
import { API_CONFIG } from '@/lib/api';

/*
 * A user's starred team chat messages, stored on the server (`/chat/starred`)
 * so they follow the user across browsers and devices. They used to live in
 * localStorage only; ids still there are uploaded once, then that key is
 * removed.
 *
 * One shared store per user: every component using the hook for that user
 * sees the same set and updates together.
 */

const LEGACY_STORAGE_PREFIX = 'chat:starred:';
const CHANGE_EVENT = 'chat:starred-changed';

type Store = {
  userId: string;
  ids: string[];
  loading: Promise<void> | null;
  /*
   * Changes made while the first load is in flight - the last one per message
   * (true = starred), and whether everything was cleared. Applied on top of
   * what the load returns, so a star toggled meanwhile is not lost.
   */
  edits: Map<string, boolean>;
  clearedWhileLoading: boolean;
  /** The last load failed: the next screen that needs the store loads again. */
  failed: boolean;
};
const stores = new Map<string, Store>();

/** Test helper: forget every loaded store. */
export function __resetStarredStoresForTests() {
  stores.clear();
}

function notify(userId: string) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { userId } }));
}

async function api(path: string, method: string): Promise<any> {
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Not signed in');
  const res = await fetch(`${API_CONFIG.BASE_URL}/chat/starred${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw Object.assign(new Error(json?.error || `HTTP ${res.status}`), { status: res.status });
  }
  return json;
}

function readLegacyIds(userId: string): string[] {
  try {
    const raw = localStorage.getItem(`${LEGACY_STORAGE_PREFIX}${userId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Stars kept in this browser before they moved to the server. The key is
 * removed only once nothing is left to retry: a star the server refuses
 * (a message that no longer exists, or in a room the user left) is dropped,
 * but one that failed for a network or server error stays for the next load.
 */
async function uploadLegacyStars(userId: string, serverIds: Set<string>): Promise<string[]> {
  const key = `${LEGACY_STORAGE_PREFIX}${userId}`;
  const legacy = readLegacyIds(userId).filter((id) => !serverIds.has(id));
  const uploaded: string[] = [];
  const retry: string[] = [];
  for (const id of legacy) {
    try {
      await api(`/${encodeURIComponent(id)}`, 'PUT');
      uploaded.push(id);
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (!status || status >= 500) retry.push(id);
    }
  }
  try {
    if (retry.length) localStorage.setItem(key, JSON.stringify(retry));
    else localStorage.removeItem(key);
  } catch {
    // ignore
  }
  return uploaded;
}

function ensureLoaded(userId: string): Store {
  const existing = stores.get(userId);
  if (existing && (existing.loading || !existing.failed)) return existing;
  // A failed store is loaded again in place: toggles keep working on it, and
  // changes made since are kept as edits (merged when the load lands).
  const next: Store = existing ?? {
    userId, ids: [], loading: null, edits: new Map(), clearedWhileLoading: false, failed: false,
  };
  if (existing) {
    for (const id of next.ids) if (!next.edits.has(id)) next.edits.set(id, true);
  }
  next.failed = false;
  stores.set(userId, next);
  next.loading = (async () => {
    try {
      const json = await api('', 'GET');
      const ids: string[] = (Array.isArray(json?.data) ? json.data : [])
        .map((row: { message_id?: string }) => row?.message_id)
        .filter((id: unknown): id is string => typeof id === 'string');
      const uploaded = await uploadLegacyStars(userId, new Set(ids));
      if (stores.get(userId) === next) {
        const merged = new Set(next.clearedWhileLoading ? [] : [...ids, ...uploaded]);
        for (const [id, starred] of next.edits) {
          if (starred) merged.add(id);
          else merged.delete(id);
        }
        next.ids = [...merged];
        notify(userId);
      }
    } catch (error) {
      console.error('[useStarredMessages] Could not load starred messages:', error);
      // Not loaded: the next screen that needs the store retries. The stars
      // shown meanwhile (toggled while loading) stay on screen.
      next.failed = true;
    } finally {
      next.loading = null;
      next.edits.clear();
      next.clearedWhileLoading = false;
    }
  })();
  return next;
}

export interface UseStarredMessagesResult {
  starredIds: Set<string>;
  isStarred: (messageId: string) => boolean;
  toggleStar: (messageId: string) => void;
  clearStars: () => void;
}

export function useStarredMessages(userId: string | null | undefined): UseStarredMessagesResult {
  const [ids, setIds] = useState<string[]>(() => (userId ? stores.get(userId)?.ids ?? [] : []));

  useEffect(() => {
    if (!userId) {
      setIds([]);
      return;
    }
    const current = ensureLoaded(userId);
    setIds(current.ids);

    const onChange = (e: Event) => {
      const changed = (e as CustomEvent<{ userId?: string }>).detail?.userId;
      if (changed && changed !== userId) return;
      setIds(stores.get(userId)?.ids ?? []);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [userId]);

  const starredIds = new Set(ids);

  const isStarred = useCallback(
    (messageId: string) => starredIds.has(messageId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids],
  );

  const toggleStar = useCallback(
    (messageId: string) => {
      const s = userId ? stores.get(userId) : undefined;
      if (!userId || !messageId || !s) return;
      const wasStarred = s.ids.includes(messageId);
      const before = s.ids;
      // Show the change at once; undo it if the server refuses.
      s.ids = wasStarred ? s.ids.filter((id) => id !== messageId) : [...s.ids, messageId];
      if (s.loading) s.edits.set(messageId, !wasStarred);
      notify(userId);
      api(`/${encodeURIComponent(messageId)}`, wasStarred ? 'DELETE' : 'PUT').catch((error) => {
        console.error('[useStarredMessages] Could not update star:', error);
        if (stores.get(userId) === s) {
          s.ids = before;
          // Refused: the server keeps its own state, so the load's is right.
          s.edits.delete(messageId);
          notify(userId);
        }
      });
    },
    [userId],
  );

  const clearStars = useCallback(() => {
    const s = userId ? stores.get(userId) : undefined;
    if (!userId || !s) return;
    const before = s.ids;
    s.ids = [];
    if (s.loading) {
      s.edits.clear();
      s.clearedWhileLoading = true;
    }
    notify(userId);
    api('', 'DELETE').catch((error) => {
      console.error('[useStarredMessages] Could not clear stars:', error);
      if (stores.get(userId) === s) {
        s.ids = before;
        s.clearedWhileLoading = false;
        notify(userId);
      }
    });
  }, [userId]);

  return { starredIds, isStarred, toggleStar, clearStars };
}
